import { useEffect, useState } from 'react';
import { api } from '../../api.js';
import { Spinner, ErrorNote } from '../../components.jsx';

const POLL_MS = 5000;

const DIRECTION = {
  inbound: { label: '↙ IN', cls: 'bg-navy/15 text-navy dark:bg-steel/25 dark:text-mist' },
  outbound: { label: '↗ OUT', cls: 'bg-amber/30 text-charcoal dark:text-amber' },
  internal: { label: '⇄ INT', cls: 'bg-steel/20 text-steel' },
};

function fmtElapsed(sec) {
  sec = Math.max(0, Math.floor(sec));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const p = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${p(m)}:${p(s)}` : `${p(m)}:${p(s)}`;
}

// Live "who's on the phone right now" board, fed by the Maxotel-Calls-App
// via this app's own server (never called directly from the browser). Only
// active calls -- idle extensions aren't tracked. No internal title: it's
// always wrapped by CollapsibleSection, which owns that.
export default function PhoneLiveWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Server clock minus ours, so the per-call timers are right even if this
  // device's clock is off -- same trick the Maxotel page itself uses.
  const [offset, setOffset] = useState(0);
  const [, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      if (document.hidden) return;
      api
        .get('/api/widgets/phone-live')
        .then((d) => {
          if (cancelled) return;
          setData(d);
          setOffset(d.now - Date.now() / 1000);
          setError(null);
        })
        .catch((e) => {
          if (!cancelled) setError(e.message);
        });
    };
    load();
    const id = setInterval(load, POLL_MS);
    document.addEventListener('visibilitychange', load); // refresh straight away on returning to the tab
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', load);
    };
  }, []);

  const calls = data?.calls ?? [];

  // Re-render once a second so the timers tick, but only while there's a
  // call to time.
  useEffect(() => {
    if (!calls.length) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [calls.length]);

  const nowSec = Date.now() / 1000 + offset;

  return (
    <div className="card p-4">
      {error && <ErrorNote>Couldn't reach the phone system ({error})</ErrorNote>}
      {!data && !error && <Spinner label="Loading calls…" />}

      {/* On error the last board is hidden rather than left up -- a frozen
          list of "live" calls would be actively misleading. */}
      {data && !error && (
        <>
          <div className={`mb-3 text-sm font-bold ${calls.length ? 'text-[#4f7a4a] dark:text-[#8fbf86]' : 'text-steel'}`}>
            {calls.length ? `${calls.length} on a call` : 'No one is on the phone'}
          </div>

          {calls.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              {calls.map((c) => {
                const dir = DIRECTION[c.direction] || DIRECTION.internal;
                return (
                  <div
                    key={c.ext}
                    className={`rounded-lg border p-3 text-sm ${c.transferred ? 'border-amber/60' : 'border-steel/15'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-semibold">{c.name || `Ext ${c.ext}`}</div>
                        <div className="text-xs text-steel">ext {c.ext}</div>
                      </div>
                      <div className="shrink-0 font-mono text-lg font-semibold text-[#4f7a4a] dark:text-[#8fbf86]">
                        {fmtElapsed(nowSec - c.since)}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      <span className={`badge ${dir.cls}`}>{dir.label}</span>
                      <span className="font-mono">{c.party || 'unknown'}</span>
                      {c.party_name && <span className="text-steel">{c.party_name}</span>}
                      {c.transferred && <span className="badge bg-amber/30 text-charcoal dark:text-amber">transferred</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <p className="mt-3 text-xs text-steel">
            {!data.enabled
              ? 'Live calls are switched off on the phone app (no webhook token set).'
              : !data.last_event_at
              ? 'No webhook events received yet.'
              : `Last event ${new Date(data.last_event_at * 1000).toLocaleTimeString()}`}
          </p>
        </>
      )}
    </div>
  );
}
